<template>
  <div class="container-fluid py-3" style="max-width: 800px">
    <h4 class="mb-4">Settings</h4>

    <!-- ── Custom Tokens ─────────────────────────────────── -->
    <div class="card mb-4">
      <div class="card-header font-weight-bold">Custom Tokens</div>
      <div class="card-body">
        <div v-for="chain in evmChains" :key="chain.key" class="mb-4">
          <h6 :style="`color:${chain.color}`">{{ chain.label }} tokens</h6>
          <table class="table table-sm table-bordered mb-2">
            <thead><tr><th>Ticker</th><th>Contract</th><th>Decimals</th><th></th></tr></thead>
            <tbody>
              <tr v-if="!customTokens[chain.key] || !customTokens[chain.key].length">
                <td colspan="4" class="text-muted small">No custom tokens</td>
              </tr>
              <tr v-for="t in customTokens[chain.key] || []" :key="t.address">
                <td>{{ t.ticker }}</td>
                <td class="small text-break">{{ t.address }}</td>
                <td>{{ t.decimals }}</td>
                <td>
                  <button class="btn btn-danger btn-sm" @click="removeToken(chain.key, t.address)">Remove</button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Add token form -->
          <div v-if="addingChain !== chain.key">
            <button class="btn btn-sm btn-outline-primary" @click="startAdd(chain.key)">+ Add Token</button>
          </div>
          <div v-else class="form-inline" style="gap:6px; flex-wrap:wrap">
            <input class="form-control form-control-sm" v-model="newToken.ticker" placeholder="Ticker (e.g. ARB)" style="width:100px" />
            <input class="form-control form-control-sm" v-model="newToken.address" placeholder="Contract address" style="width:320px" />
            <input class="form-control form-control-sm" v-model.number="newToken.decimals" type="number" placeholder="Decimals" style="width:90px" />
            <button class="btn btn-sm btn-primary" @click="saveToken(chain.key)">Save</button>
            <button class="btn btn-sm btn-secondary" @click="addingChain = null">Cancel</button>
          </div>
        </div>

        <div class="mt-3">
          <h6 style="color:#FF0013">Tron TRC-20 tokens</h6>
          <table class="table table-sm table-bordered mb-2">
            <thead><tr><th>Ticker</th><th>Contract</th><th>Decimals</th><th></th></tr></thead>
            <tbody>
              <tr v-if="!customTokens.tron || !customTokens.tron.length">
                <td colspan="4" class="text-muted small">No custom tokens</td>
              </tr>
              <tr v-for="t in customTokens.tron || []" :key="t.address">
                <td>{{ t.ticker }}</td>
                <td class="small text-break">{{ t.address }}</td>
                <td>{{ t.decimals }}</td>
                <td><button class="btn btn-danger btn-sm" @click="removeToken('tron', t.address)">Remove</button></td>
              </tr>
            </tbody>
          </table>
          <div v-if="addingChain !== 'tron'">
            <button class="btn btn-sm btn-outline-primary" @click="startAdd('tron')">+ Add TRC-20 Token</button>
          </div>
          <div v-else class="form-inline" style="gap:6px; flex-wrap:wrap">
            <input class="form-control form-control-sm" v-model="newToken.ticker" placeholder="Ticker" style="width:100px" />
            <input class="form-control form-control-sm" v-model="newToken.address" placeholder="TRC-20 Contract address" style="width:320px" />
            <input class="form-control form-control-sm" v-model.number="newToken.decimals" type="number" placeholder="Decimals" style="width:90px" />
            <button class="btn btn-sm btn-primary" @click="saveToken('tron')">Save</button>
            <button class="btn btn-sm btn-secondary" @click="addingChain = null">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Account ───────────────────────────────────────── -->
    <div class="card mb-4">
      <div class="card-header font-weight-bold">Account</div>
      <div class="card-body">
        <div v-if="currentUser" class="mb-3">
          <p class="small mb-1"><strong>Email:</strong> {{ currentUser.email }}</p>
          <p v-if="currentUser.name" class="small mb-0"><strong>Name:</strong> {{ currentUser.name }}</p>
        </div>
        <p class="text-muted small">Change your account password.</p>
        <div class="form-group" style="max-width:280px">
          <label>Current Password</label>
          <input type="password" class="form-control" v-model="oldPwd" placeholder="Current password" />
        </div>
        <div class="form-group" style="max-width:280px">
          <label>New Password</label>
          <input type="password" class="form-control" v-model="newPwd" placeholder="New password" />
        </div>
        <div class="form-group" style="max-width:280px">
          <label>Confirm Password</label>
          <input type="password" class="form-control" v-model="confirmPwd" placeholder="Confirm password" />
        </div>
        <button class="btn btn-warning" @click="changePassword" :disabled="pwdLoading">
          {{ pwdLoading ? 'Saving…' : 'Change Password' }}
        </button>
        <span v-if="pwdMsg" class="ml-3 small" :class="pwdOk ? 'text-success' : 'text-danger'">{{ pwdMsg }}</span>
      </div>
    </div>

    <!-- ── About ─────────────────────────────────────────── -->
    <div class="card">
      <div class="card-header font-weight-bold">About</div>
      <div class="card-body small text-muted">
        <p><strong>HDWallet</strong> v{{ version }} — Multi-chain Ledger HD Wallet Manager</p>
        <p>Chains: Tron, Solana, Ethereum, BSC, Polygon, Bitcoin</p>
        <p>Built with Vue 2, ethers.js, @ledgerhq, Chart.js, Bootstrap 4</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getCustomTokens, addCustomToken, removeCustomToken, getAllCustomTokens } from '../services/tokenService';
import { VERSION } from '../version';
import { changePassword, pb } from '../services/pbService';

export default {
  name: 'Settings',
  data() {
    return {
      version: VERSION,
      customTokens: getAllCustomTokens(),
      addingChain: null,
      newToken: { ticker: '', address: '', decimals: 18 },
      evmChains: [
        { key: 'ethereum', label: 'Ethereum', color: '#627EEA' },
        { key: 'bsc',      label: 'BSC',      color: '#F0B90B' },
        { key: 'polygon',  label: 'Polygon',  color: '#8247E5' },
      ],
      oldPwd: '',
      newPwd: '',
      confirmPwd: '',
      pwdMsg: '',
      pwdOk: false,
      pwdLoading: false,
      currentUser: pb.authStore.record,
    };
  },
  methods: {
    startAdd(chain) {
      this.addingChain = chain;
      this.newToken = { ticker: '', address: '', decimals: 18 };
    },
    saveToken(chain) {
      if (!this.newToken.ticker || !this.newToken.address) {
        alert('Ticker and address are required.');
        return;
      }
      addCustomToken(chain, { ...this.newToken, type: ['ethereum','bsc','polygon'].includes(chain) ? 'erc20' : 'trc20' });
      this.customTokens = getAllCustomTokens();
      this.addingChain = null;
    },
    removeToken(chain, address) {
      removeCustomToken(chain, address);
      this.customTokens = getAllCustomTokens();
    },
    async changePassword() {
      if (!this.oldPwd) { this.pwdMsg = 'Current password is required.'; this.pwdOk = false; return; }
      if (!this.newPwd) { this.pwdMsg = 'New password cannot be empty.'; this.pwdOk = false; return; }
      if (this.newPwd !== this.confirmPwd) { this.pwdMsg = 'Passwords do not match.'; this.pwdOk = false; return; }
      this.pwdLoading = true;
      try {
        await changePassword(this.oldPwd, this.newPwd);
        this.pwdMsg = 'Password updated successfully.';
        this.pwdOk = true;
        this.oldPwd = '';
        this.newPwd = '';
        this.confirmPwd = '';
      } catch (e) {
        this.pwdMsg = e?.response?.data?.message || e?.message || 'Password change failed.';
        this.pwdOk = false;
      } finally {
        this.pwdLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.container-fluid { padding: 0 !important; }
h4 { font-size: 1.1rem !important; font-weight: 800 !important; color: var(--text, #c8cfe8) !important; letter-spacing: -0.01em; }

.card { margin-bottom: 20px !important; }
.card-header { text-transform: uppercase !important; }

/* Token tables */
.table-sm thead th { background: var(--surface-2, #171b28) !important; }
.table-bordered td, .table-bordered th { border-color: var(--border, #242a3a) !important; }

/* Chain labels */
h6 { font-size: 0.82rem !important; font-weight: 700 !important; letter-spacing: 0.04em; margin-bottom: 10px !important; }

/* Inline add-token form */
.form-inline { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-start; }

/* Passwords */
.form-group input[type="password"] { letter-spacing: 0.15em; }
</style>
